/**
 * Auto Adrenaline — main.js
 * Core site interactions. Vanilla JS only. No dependencies.
 * Initialisation order:
 *  1. Page loader (rev meter)
 *  2. Custom cursor + trail
 *  3. Navbar scroll + mobile drawer
 *  4. Search drawer
 *  5. Cart drawer (AJAX Cart API)
 *  6. Scroll reveal (IntersectionObserver)
 *  7. Back-to-top button
 *  8. 3D card tilt (data-tilt)
 *  9. Quantity input controls
 * 10. Announcement bar height offset
 */

(function () {
  'use strict';

  /* ─────────────────────────────────────────────────────────
     HELPERS
  ───────────────────────────────────────────────────────── */

  var AA = window.AutoAdrenaline || {};
  window.AutoAdrenaline = AA;

  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return Array.from((ctx || document).querySelectorAll(sel)); }

  function formatMoney(cents) {
    var fmt = AA.moneyFormat || '${{amount}}';
    var amount = (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return fmt.replace('{{amount}}', amount)
              .replace('{{amount_no_decimals}}', Math.round(cents / 100));
  }

  function debounce(fn, ms) {
    var t;
    return function () {
      clearTimeout(t);
      t = setTimeout(fn.bind(this, arguments), ms);
    };
  }

  /* ─────────────────────────────────────────────────────────
     1. PAGE LOADER — Rev meter fills as DOM + assets load
  ───────────────────────────────────────────────────────── */

  var loader   = document.getElementById('page-loader');
  var revFill  = document.getElementById('rev-fill');
  var rpmCounter = document.getElementById('rpm-counter');
  var redlineBar = loader ? loader.querySelector('.loader__redline-bar') : null;

  if (loader) {
    var progress = 0;
    var targetRPM = 8500;
    var animating = true;

    function animateRev(target) {
      var start     = progress;
      var startTime = null;
      var duration  = 900;

      function step(ts) {
        if (!startTime) startTime = ts;
        var elapsed = ts - startTime;
        var pct     = Math.min(elapsed / duration, 1);
        /* Ease out expo */
        var ease    = pct === 1 ? 1 : 1 - Math.pow(2, -10 * pct);
        progress    = start + (target - start) * ease;

        if (revFill)    revFill.style.width = progress + '%';
        if (rpmCounter) rpmCounter.textContent = Math.round((progress / 100) * targetRPM).toLocaleString();
        if (redlineBar) redlineBar.style.width = progress + '%';

        if (pct < 1 && animating) requestAnimationFrame(step);
      }
      requestAnimationFrame(step);
    }

    /* Staged rev animation */
    animateRev(60);
    setTimeout(function () { animateRev(85); }, 400);
    setTimeout(function () { animateRev(100); }, 800);

    function hideLoader() {
      animating = false;
      if (revFill)    revFill.style.width = '100%';
      if (rpmCounter) rpmCounter.textContent = targetRPM.toLocaleString();

      setTimeout(function () {
        loader.classList.add('loaded');
        document.body.classList.add('page-ready');
      }, 300);
    }

    if (document.readyState === 'complete') {
      setTimeout(hideLoader, 900);
    } else {
      window.addEventListener('load', function () { setTimeout(hideLoader, 300); });
      /* Safety fallback */
      setTimeout(hideLoader, 3000);
    }
  }

  /* ─────────────────────────────────────────────────────────
     2. CUSTOM CURSOR + TRAIL
  ───────────────────────────────────────────────────────── */

  var cursorDot  = $('.cursor__dot');
  var cursorRing = $('.cursor__ring');
  var mouseX = 0, mouseY = 0;
  var dotX   = 0, dotY   = 0;
  var ringX  = 0, ringY  = 0;
  var TRAIL_MAX  = 8;
  var trailDots  = [];
  var trailPositions = [];
  var cursorRAF;

  /* Only on non-touch devices */
  if (cursorDot && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {

    /* Dot follows cursor instantly */
    document.addEventListener('mousemove', function (e) {
      mouseX = e.clientX;
      mouseY = e.clientY;
      trailPositions.unshift({ x: mouseX, y: mouseY });
      if (trailPositions.length > TRAIL_MAX) trailPositions.pop();
    });

    /* Ring lerps behind */
    function tickCursor() {
      dotX  += (mouseX - dotX)  * 0.8;
      dotY  += (mouseY - dotY)  * 0.8;
      ringX += (mouseX - ringX) * 0.18;
      ringY += (mouseY - ringY) * 0.18;

      cursorDot.style.transform  = 'translate(' + dotX  + 'px, ' + dotY  + 'px) translate(-50%, -50%)';
      cursorRing.style.transform = 'translate(' + ringX + 'px, ' + ringY + 'px) translate(-50%, -50%)';

      /* Trail dots */
      trailDots.forEach(function (dot, i) {
        var pos = trailPositions[i + 1];
        if (!pos) { dot.style.opacity = '0'; return; }
        dot.style.opacity   = (1 - i / TRAIL_MAX) * 0.5 + '';
        dot.style.transform = 'translate(' + pos.x + 'px, ' + pos.y + 'px) translate(-50%, -50%)';
      });

      cursorRAF = requestAnimationFrame(tickCursor);
    }

    /* Create trail dots */
    for (var ti = 0; ti < TRAIL_MAX - 1; ti++) {
      var td = document.createElement('div');
      td.className = 'cursor-trail-dot';
      td.style.cssText = 'position:fixed;width:' + (4 - ti * 0.3) + 'px;height:' + (4 - ti * 0.3) + 'px;background:var(--color-red);border-radius:50%;pointer-events:none;z-index:9990;transform:translate(-50%,-50%);opacity:0;transition:none';
      document.body.appendChild(td);
      trailDots.push(td);
    }

    tickCursor();

    /* Hover state on interactive elements */
    var interactiveSelectors = 'a, button, [role="button"], input, select, textarea, label, [data-tilt], .product-card, .category-card';

    document.addEventListener('mouseover', function (e) {
      if (e.target.closest(interactiveSelectors)) {
        document.body.classList.add('cursor-hover');
      }
    });

    document.addEventListener('mouseout', function (e) {
      if (e.target.closest(interactiveSelectors)) {
        document.body.classList.remove('cursor-hover');
      }
    });

    /* Hide native cursor when JS cursor is active */
    document.documentElement.style.cursor = 'none';

    /* Pause when tab is hidden */
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        cancelAnimationFrame(cursorRAF);
      } else {
        tickCursor();
      }
    });
  }

  /* ─────────────────────────────────────────────────────────
     3. NAVBAR SCROLL + MOBILE DRAWER
  ───────────────────────────────────────────────────────── */

  var siteNav      = document.getElementById('site-header');
  var hamburger    = document.getElementById('nav-hamburger');
  var mobileDrawer = document.getElementById('mobile-drawer');
  var mobileOverlay = document.getElementById('mobile-overlay');
  var lastScrollY  = 0;

  if (siteNav) {
    function onScroll() {
      var y = window.scrollY;

      /* Add scrolled class for frosted glass */
      siteNav.classList.toggle('scrolled', y > 10);

      /* Auto-hide nav on aggressive scroll down (optional) */
      if (y > 150 && y > lastScrollY + 60) {
        siteNav.style.transform = 'translateY(-100%)';
      } else if (y < lastScrollY - 10 || y < 80) {
        siteNav.style.transform = 'translateY(0)';
      }
      lastScrollY = y;
    }

    /* Smooth transition only after first scroll */
    window.addEventListener('scroll', onScroll, { passive: true });
    siteNav.style.transition = 'transform 0.4s var(--ease-out), background 0.3s, border-color 0.3s';
    onScroll();
  }

  function openMobileNav() {
    if (!mobileDrawer) return;
    mobileDrawer.classList.add('open');
    mobileDrawer.setAttribute('aria-hidden', 'false');
    if (mobileOverlay) mobileOverlay.classList.add('open');
    if (hamburger) {
      hamburger.classList.add('open');
      hamburger.setAttribute('aria-expanded', 'true');
      hamburger.setAttribute('aria-label', 'Close navigation menu');
    }
    document.body.style.overflow = 'hidden';
  }

  function closeMobileNav() {
    if (!mobileDrawer) return;
    mobileDrawer.classList.remove('open');
    mobileDrawer.setAttribute('aria-hidden', 'true');
    if (mobileOverlay) mobileOverlay.classList.remove('open');
    if (hamburger) {
      hamburger.classList.remove('open');
      hamburger.setAttribute('aria-expanded', 'false');
      hamburger.setAttribute('aria-label', 'Open navigation menu');
    }
    document.body.style.overflow = '';
  }

  if (hamburger) {
    hamburger.addEventListener('click', function () {
      mobileDrawer && mobileDrawer.classList.contains('open')
        ? closeMobileNav()
        : openMobileNav();
    });
  }

  if (mobileOverlay) mobileOverlay.addEventListener('click', closeMobileNav);

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
      closeMobileNav();
      closeSearchDrawer();
    }
  });

  /* ─────────────────────────────────────────────────────────
     4. SEARCH DRAWER
  ───────────────────────────────────────────────────────── */

  var searchToggle  = document.getElementById('search-toggle');
  var searchDrawer  = document.getElementById('search-drawer');
  var searchInput   = document.getElementById('search-input');
  var searchClear   = document.getElementById('search-clear');
  var searchResults = document.getElementById('search-results');

  // Move drawer to <body> once on init so the first tap is instant (no DOM reflow on click)
  if (searchDrawer && searchDrawer.parentElement !== document.body) document.body.appendChild(searchDrawer);

  function openSearchDrawer() {
    if (!searchDrawer) return;
    searchDrawer.classList.add('open');
    searchDrawer.getAnimations().forEach(function(a){ a.cancel(); });
    searchDrawer.style.opacity = '1'; searchDrawer.style.transform = 'translateY(0)'; searchDrawer.style.position = 'fixed'; searchDrawer.style.zIndex = '9999'; searchDrawer.style.top = '63px'; searchDrawer.style.left = '0'; searchDrawer.style.right = '0';
    searchDrawer.setAttribute('aria-hidden', 'false');
    if (searchToggle) searchToggle.setAttribute('aria-expanded', 'true');
    setTimeout(function () { if (searchInput) searchInput.focus(); }, 50);
  }

  function closeSearchDrawer() {
    if (!searchDrawer) return;
    searchDrawer.classList.remove('open');
    searchDrawer.getAnimations().forEach(function(a){ a.cancel(); });
    searchDrawer.style.opacity = ''; searchDrawer.style.transform = ''; searchDrawer.style.position = ''; searchDrawer.style.zIndex = ''; searchDrawer.style.top = ''; searchDrawer.style.left = ''; searchDrawer.style.right = '';
    searchDrawer.setAttribute('aria-hidden', 'true');
    if (searchToggle) searchToggle.setAttribute('aria-expanded', 'false');
  }

      if (searchToggle) { searchToggle.addEventListener('click', function () { var n = Date.now(); if (n - (window._searchToggleLast||0) < 350) return; window._searchToggleLast = n; (searchDrawer && searchDrawer.classList.contains('open')) ? closeSearchDrawer() : openSearchDrawer(); }); }

  /* Predictive search */
  if (searchInput) {
    var searchDebounce;
    searchInput.addEventListener('input', function () {
      var q = searchInput.value.trim();

      if (searchClear) searchClear.classList.toggle('visible', q.length > 0);

      if (q.length < 2) {
        if (searchResults) searchResults.innerHTML = '';
        return;
      }

      clearTimeout(searchDebounce);
      searchDebounce = setTimeout(function () {
        runPredictiveSearch(q);
      }, 280);
    });
  }

  if (searchClear) {
    searchClear.addEventListener('click', function () {
      if (searchInput) { searchInput.value = ''; searchInput.focus(); }
      if (searchResults) searchResults.innerHTML = '';
      searchClear.classList.remove('visible');
    });
  }

  function runPredictiveSearch(query) {
    var url = '/search/suggest.json?q=' + encodeURIComponent(query) + '&resources[type]=product&resources[limit]=8';

    fetch(url, { headers: { 'Accept': 'application/json' } })
    .then(function (r) { return r.json(); })
    .then(function (data) {
      var products = data.resources && data.resources.results && data.resources.results.products;
      if (!searchResults) return;

      if (!products || products.length === 0) {
        searchResults.innerHTML = '<p style="grid-column:1/-1;color:var(--color-text-dim);font-size:0.85rem;text-align:center;padding:var(--space-md)">No results for "' + query + '"</p>';
        return;
      }

      searchResults.innerHTML = products.map(function (p) {
        var img    = p.featured_image ? '<img src="' + p.featured_image.url + '" alt="' + (p.featured_image.alt || p.title) + '" loading="lazy" width="80" height="80" style="width:80px;height:80px;object-fit:cover;border-radius:var(--radius-sm);flex-shrink:0">' : '';
        var price  = p.price ? price=p.price?p.price:"" : '';
        return '<a href="' + p.url + '" class="search-result-card" style="display:flex;gap:0.75rem;align-items:center;padding:0.75rem;background:rgba(255,255,255,0.03);border:1px solid rgba(255,255,255,0.06);border-radius:var(--radius-sm);transition:background 0.15s,border-color 0.15s" onmouseover="this.style.background=\'rgba(255,0,0,0.06)\';this.style.borderColor=\'rgba(255,0,0,0.3)\'" onmouseout="this.style.background=\'rgba(255,255,255,0.03)\';this.style.borderColor=\'rgba(255,255,255,0.06)\'">'
          + img
          + '<div style="min-width:0"><p style="font-family:var(--font-heading);font-size:0.82rem;font-weight:600;text-transform:uppercase;letter-spacing:0.05em;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;margin-bottom:0.2rem">' + p.title + '</p>'
          + '<p style="font-family:var(--font-heading);font-size:0.85rem;color:var(--color-red)">' + price + '</p>'
          + '</div>'
          + '</a>';
      }).join('');
    })
    .catch(function () {
      /* Predictive search silently fails — user can still submit the form */
    });
  }

  /* Close search on outside click */
  document.addEventListener('click', function (e) {
    if (searchDrawer && searchDrawer.classList.contains('open')) {
          if (!searchDrawer.contains(e.target) && !(searchToggle && searchToggle.contains(e.target))) {
        closeSearchDrawer();
      }
    }
  });

  /* ─────────────────────────────────────────────────────────
     5. CART DRAWER — Shopify AJAX Cart API
  ───────────────────────────────────────────────────────── */

  var cartDrawer    = document.getElementById('cart-drawer');
  var cartOverlay   = document.getElementById('cart-drawer-overlay');
  var cartClose     = document.getElementById('cart-drawer-close');
  var cartToggle    = $('[data-cart-toggle]');
  var cartBadge     = document.getElementById('cart-count-badge');
  var cartBody      = document.getElementById('cart-items-container');
  var cartFooter    = document.getElementById('cart-drawer-footer');
  var cartLoading   = document.getElementById('cart-loading');
  var cartSubtotal  = document.getElementById('cart-subtotal-price');
  var cartUpsell    = document.getElementById('cart-upsell');
  var cartUpsellProduct = document.getElementById('cart-upsell-product');

  function openCart() {
    if (!cartDrawer) return;
    cartDrawer.classList.add('open');
    cartDrawer.setAttribute('aria-hidden', 'false');
    if (cartOverlay) cartOverlay.classList.add('open');
    document.body.style.overflow = 'hidden';
    if (cartToggle) cartToggle.setAttribute('aria-expanded', 'true');
    fetchCart();
  }

  function closeCart() {
    if (!cartDrawer) return;
    cartDrawer.classList.remove('open');
    cartDrawer.setAttribute('aria-hidden', 'true');
    if (cartOverlay) cartOverlay.classList.remove('open');
    document.body.style.overflow = '';
    if (cartToggle) cartToggle.setAttribute('aria-expanded', 'false');
  }

  AA.openCart  = openCart;
  AA.closeCart = closeCart;

  if (cartToggle) cartToggle.addEventListener('click', openCart);
  if (cartClose)  cartClose.addEventListener('click', closeCart);
  if (cartOverlay) cartOverlay.addEventListener('click', closeCart);

  function fetchCart() {
    if (cartLoading) cartLoading.style.display = 'flex';
    if (cartBody)    cartBody.innerHTML = '';
    if (cartFooter)  cartFooter.style.display = 'none';

    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
    .then(function (r) { return r.json(); })
    .then(function (cart) {
      renderCartDrawer(cart);
    })
    .catch(function () {
      if (cartLoading) cartLoading.style.display = 'none';
      if (cartBody)    cartBody.innerHTML = '<p style="text-align:center;color:var(--color-text-dim);padding:var(--space-lg)">Could not load cart. Please try again.</p>';
    });
  }

  function renderCartDrawer(cart) {
    if (cartLoading) cartLoading.style.display = 'none';
    updateCartBadge(cart.item_count);

    if (!cartBody) return;

    if (cart.item_count === 0) {
      cartBody.innerHTML = buildEmptyCart();
      if (cartFooter) cartFooter.style.display = 'none';
      return;
    }

    cartBody.innerHTML = cart.items.map(function (item, idx) {
      return buildCartItem(item, idx);
    }).join('');

    if (cartSubtotal) cartSubtotal.textContent = formatMoney(cart.total_price);
    if (cartFooter)   cartFooter.style.display = 'block';

    /* Bind qty + remove handlers */
    bindCartItemHandlers();

    /* Upsell — show first product from a "upsell" collection if available */
    loadUpsell(cart);
  }

  function buildEmptyCart() {
    return '<div class="cart-drawer__empty">'
      + '<div class="cart-drawer__empty-icon">'
      + '<svg viewBox="0 0 64 64" fill="none" width="64" height="64"><path d="M8 8h4.5l5.4 25.5a4.5 4.5 0 004.4 3.6h22.5a4.5 4.5 0 004.35-3.36L52 19H20" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><circle cx="26" cy="52" r="3.6" stroke="currentColor" stroke-width="2"/><circle cx="42" cy="52" r="3.6" stroke="currentColor" stroke-width="2"/></svg>'
      + '</div>'
      + '<p class="cart-drawer__empty-text">Your cart is empty</p>'
      + '<p style="font-size:0.8rem;color:var(--color-text-dim);margin-bottom:var(--space-md)">Time to build something epic.</p>'
      + '<a href="/collections/all" class="btn btn--primary" onclick="window.AutoAdrenaline.closeCart()">Shop Now</a>'
      + '</div>';
  }

  function buildCartItem(item, idx) {
    var img = item.featured_image && item.featured_image.url
      ? '<img src="' + item.featured_image.url + '" alt="' + (item.featured_image.alt || item.title) + '" loading="lazy" class="cart-item__img" width="72" height="72">'
      : '<div class="cart-item__img" style="background:var(--color-bg)"></div>';

    var variantTitle = item.variant_title && item.variant_title !== 'Default Title'
      ? '<p class="cart-item__variant">' + item.variant_title + '</p>'
      : '';

    return '<div class="cart-item" data-item-key="' + item.key + '" data-item-idx="' + idx + '">'
      + '<a href="' + item.url + '" aria-label="' + item.title + '">' + img + '</a>'
      + '<div class="cart-item__info">'
      + '<p class="cart-item__title"><a href="' + item.url + '">' + item.title + '</a></p>'
      + variantTitle
      + '<div class="cart-item__price-row">'
      + '<span class="cart-item__price">' + formatMoney(item.final_line_price) + '</span>'
      + '<div style="display:flex;align-items:center;gap:0.5rem">'
      + '<div class="qty-input" style="height:32px" aria-label="Item quantity">'
      + '<button type="button" class="qty-input__btn" style="height:32px;width:32px" data-cart-qty-minus data-line="' + (idx + 1) + '" aria-label="Decrease quantity">−</button>'
      + '<input type="number" class="qty-input__field" style="height:32px;width:36px;font-size:0.85rem" value="' + item.quantity + '" min="0" data-cart-qty-input data-line="' + (idx + 1) + '" aria-label="Item quantity">'
      + '<button type="button" class="qty-input__btn" style="height:32px;width:32px" data-cart-qty-plus data-line="' + (idx + 1) + '" aria-label="Increase quantity">+</button>'
      + '</div>'
      + '<button type="button" class="cart-item__remove" data-cart-remove data-line="' + (idx + 1) + '" aria-label="Remove ' + item.title + ' from cart">Remove</button>'
      + '</div>'
      + '</div>'
      + '</div>'
      + '</div>';
  }

  function bindCartItemHandlers() {
    /* Remove */
    $$('[data-cart-remove]', cartBody).forEach(function (btn) {
      btn.addEventListener('click', function () {
        updateCartLine(parseInt(btn.dataset.line), 0);
      });
    });

    /* Qty minus */
    $$('[data-cart-qty-minus]', cartBody).forEach(function (btn) {
      btn.addEventListener('click', function () {
        var input = cartBody.querySelector('[data-cart-qty-input][data-line="' + btn.dataset.line + '"]');
        if (!input) return;
        var newQty = Math.max(0, parseInt(input.value) - 1);
        input.value = newQty;
        updateCartLine(parseInt(btn.dataset.line), newQty);
      });
    });

    /* Qty plus */
    $$('[data-cart-qty-plus]', cartBody).forEach(function (btn) {
      btn.addEventListener('click', function () {
        var input = cartBody.querySelector('[data-cart-qty-input][data-line="' + btn.dataset.line + '"]');
        if (!input) return;
        var newQty = parseInt(input.value) + 1;
        input.value = newQty;
        updateCartLine(parseInt(btn.dataset.line), newQty);
      });
    });

    /* Qty typed */
    $$('[data-cart-qty-input]', cartBody).forEach(function (input) {
      var debounced = debounce(function () {
        var qty = parseInt(input.value);
        if (!isNaN(qty) && qty >= 0) updateCartLine(parseInt(input.dataset.line), qty);
      }, 500);
      input.addEventListener('input', debounced);
    });
  }

  function updateCartLine(line, quantity) {
    var payload = { line: line, quantity: quantity };

    /* Optimistic visual feedback */
    var itemEls = cartBody ? cartBody.querySelectorAll('.cart-item') : [];
    if (itemEls[line - 1]) itemEls[line - 1].style.opacity = '0.5';

    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(payload)
    })
    .then(function (r) { return r.json(); })
    .then(function (cart) { renderCartDrawer(cart); })
    .catch(function () {
      /* Restore opacity on error */
      if (itemEls[line - 1]) itemEls[line - 1].style.opacity = '1';
    });
  }

  /* Cart upsell — fetches a recommendation from /recommendations API */
  function loadUpsell(cart) {
    if (!cartUpsell || !cartUpsellProduct || cart.items.length === 0) return;

    var firstProductId = cart.items[0].product_id;
    var upsellUrl = '/recommendations/products.json?product_id=' + firstProductId + '&limit=1&intent=related';

    fetch(upsellUrl, { headers: { 'Accept': 'application/json' } })
    .then(function (r) { return r.json(); })
    .then(function (data) {
      var rec = data.products && data.products[0];
      if (!rec) return;

      /* Don't show if product is already in cart */
      var inCart = cart.items.some(function (i) { return i.product_id === rec.id; });
      if (inCart) return;

      var img     = rec.featured_image ? '<img src="' + rec.featured_image + '" alt="' + rec.title + '" loading="lazy" style="width:52px;height:52px;object-fit:cover;border-radius:var(--radius-sm);flex-shrink:0">' : '';
      var varId   = rec.variants && rec.variants[0] ? rec.variants[0].id : '';
      var price   = rec.variants && rec.variants[0] ? formatMoney(rec.variants[0].price) : '';

      cartUpsellProduct.innerHTML = '<div style="display:flex;align-items:center;gap:0.75rem">'
        + img
        + '<div style="flex:1;min-width:0">'
        + '<p style="font-family:var(--font-heading);font-size:0.8rem;font-weight:600;text-transform:uppercase;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">' + rec.title + '</p>'
        + '<p style="font-family:var(--font-heading);font-size:0.85rem;color:var(--color-red)">' + price + '</p>'
        + '</div>'
        + '<button type="button" class="btn btn--neon btn--sm" style="flex-shrink:0;font-size:0.72rem" data-upsell-add="' + varId + '" aria-label="Add ' + rec.title + ' to cart">+ Add</button>'
        + '</div>';

      cartUpsell.style.display = 'block';

      /* Bind upsell ATC */
      var upsellBtn = cartUpsellProduct.querySelector('[data-upsell-add]');
      if (upsellBtn) {
        upsellBtn.addEventListener('click', function () {
          fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ id: parseInt(upsellBtn.dataset.upsellAdd), quantity: 1 })
          })
          .then(function (r) { return r.json(); })
          .then(function () {
            cartUpsell.style.display = 'none';
            fetchCart();
          });
        });
      }
    })
    .catch(function () { /* Upsell silently fails — not critical */ });
  }

  /* Update cart badge count everywhere */
  function updateCartBadge(count) {
    var badges = document.querySelectorAll('#cart-count-badge, .cart-count');
    badges.forEach(function (b) {
      b.textContent = count;
      b.style.display = count > 0 ? 'flex' : 'none';
      b.setAttribute('aria-label', count + ' items in cart');
    });
  }

  /* Listen for cart:updated event dispatched by product form */
  document.addEventListener('cart:updated', function () {
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
    .then(function (r) { return r.json(); })
    .then(function (cart) { updateCartBadge(cart.item_count); });
  });

  /* ─────────────────────────────────────────────────────────
     6. SCROLL REVEAL — IntersectionObserver
  ───────────────────────────────────────────────────────── */

  function initScrollReveal() {
    var revealEls   = $$('.reveal');
    var staggerEls  = $$('.reveal-stagger');

    if (revealEls.length === 0 && staggerEls.length === 0) return;

    var revealOpts = { threshold: 0.12, rootMargin: '0px 0px -40px 0px' };

    var revealObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;

        var el    = entry.target;
        var delay = el.dataset.revealDelay ? parseInt(el.dataset.revealDelay) : 0;

        setTimeout(function () {
          el.classList.add('revealed');
        }, delay);

        revealObserver.unobserve(el);
      });
    }, revealOpts);

    revealEls.forEach(function (el)  { revealObserver.observe(el); });
    staggerEls.forEach(function (el) { revealObserver.observe(el); });
  }

  /* Run after loader hides so elements aren't revealed prematurely */
  if (loader) {
    loader.addEventListener('transitionend', initScrollReveal, { once: true });
    setTimeout(initScrollReveal, 1200); /* fallback */
  } else {
    initScrollReveal();
  }

  /* ─────────────────────────────────────────────────────────
     7. BACK-TO-TOP BUTTON
  ───────────────────────────────────────────────────────── */

  var backToTop = document.getElementById('back-to-top');

  if (backToTop) {
    window.addEventListener('scroll', function () {
      backToTop.style.display = window.scrollY > 500 ? 'flex' : 'none';
    }, { passive: true });

    backToTop.addEventListener('click', function () {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  /* ─────────────────────────────────────────────────────────
     8. 3D CARD TILT — data-tilt elements
     Lightweight implementation — no library needed.
  ───────────────────────────────────────────────────────── */

  function initTilt(elements) {
    var tiltEls = elements
      ? Array.from(elements)
      : $$('[data-tilt]');

    tiltEls.forEach(function (el) {
      if (el._tiltInitialized) return;
      el._tiltInitialized = true;

      var maxTilt    = parseFloat(el.dataset.tiltMax)   || 8;
      var speed      = parseFloat(el.dataset.tiltSpeed) || 400;
      var transStr   = 'transform ' + speed + 'ms cubic-bezier(0.03,0.98,0.52,0.99)';

      function getValues(e) {
        var rect   = el.getBoundingClientRect();
        var cx     = rect.left + rect.width  / 2;
        var cy     = rect.top  + rect.height / 2;
        var clientX = e.touches ? e.touches[0].clientX : e.clientX;
        var clientY = e.touches ? e.touches[0].clientY : e.clientY;
        var rx     = ((clientY - cy) / (rect.height / 2)) * maxTilt;
        var ry     = (-(clientX - cx) / (rect.width  / 2)) * maxTilt;
        return { rx: rx, ry: ry };
      }

      el.addEventListener('mousemove', function (e) {
        el.style.transition = 'none';
        var v = getValues(e);
        el.style.transform = 'perspective(600px) rotateX(' + v.rx + 'deg) rotateY(' + v.ry + 'deg) scale3d(1.02,1.02,1.02)';
        el.style.willChange = 'transform';
      });

      el.addEventListener('mouseleave', function () {
        el.style.transition = transStr;
        el.style.transform  = 'perspective(600px) rotateX(0deg) rotateY(0deg) scale3d(1,1,1)';
        setTimeout(function () { el.style.willChange = ''; }, speed);
      });

      el.addEventListener('mouseenter', function () {
        el.style.transition = transStr;
      });
    });
  }

  AA.initTilt = initTilt;
  initTilt();

  /* ─────────────────────────────────────────────────────────
     9. QUANTITY INPUT CONTROLS (page-level, outside product page)
     Product page has its own inline handlers in product.liquid.
  ───────────────────────────────────────────────────────── */

  document.addEventListener('click', function (e) {
    /* Cart page qty minus */
    var minus = e.target.closest('[data-page-qty-minus]');
    if (minus) {
      var input = minus.closest('.qty-input').querySelector('input');
      if (input) { input.value = Math.max(1, parseInt(input.value) - 1); input.dispatchEvent(new Event('change')); }
    }

    /* Cart page qty plus */
    var plus = e.target.closest('[data-page-qty-plus]');
    if (plus) {
      var inputP = plus.closest('.qty-input').querySelector('input');
      if (inputP) { inputP.value = parseInt(inputP.value) + 1; inputP.dispatchEvent(new Event('change')); }
    }
  });

  /* ─────────────────────────────────────────────────────────
     10. PRODUCT CARD HOVER — second image swap
     Handled via CSS + the class on the img, but we need to
     make sure lazy images are preloaded on card hover.
  ───────────────────────────────────────────────────────── */

  document.addEventListener('mouseover', function (e) {
    var card = e.target.closest('.product-card');
    if (!card) return;
    var hoverImg = card.querySelector('.product-card__img--hover');
    if (hoverImg && hoverImg.loading === 'lazy') {
      hoverImg.loading = 'eager'; /* trigger lazy img load on first hover */
    }
  }, { once: false });

  /* ─────────────────────────────────────────────────────────
     11. ANNOUNCEMENT BAR HEIGHT — keep nav offset accurate
  ───────────────────────────────────────────────────────── */

  function setNavOffset() {
    var bar = document.getElementById('announcement-bar');
    if (!bar || bar.hasAttribute('data-hidden')) {
      document.documentElement.style.setProperty('--announcement-bar-height', '0px');
      return;
    }
    document.documentElement.style.setProperty('--announcement-bar-height', bar.offsetHeight + 'px');
  }

  setNavOffset();
  window.addEventListener('resize', debounce(setNavOffset, 150));

  /* ─────────────────────────────────────────────────────────
     12. KEYBOARD TRAP — cart drawer + mobile nav
  ───────────────────────────────────────────────────────── */

  function trapFocus(container) {
    var focusable = container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    var first = focusable[0];
    var last  = focusable[focusable.length - 1];

    container.addEventListener('keydown', function handler(e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last)  { e.preventDefault(); first.focus(); }
      }

      /* Remove trap when container closes */
      if (!container.classList.contains('open')) {
        container.removeEventListener('keydown', handler);
      }
    });

    if (first) first.focus();
  }

  /* Activate focus trap when cart drawer opens */
  if (cartDrawer) {
    var cartObserver = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        if (m.type === 'attributes' && m.attributeName === 'class') {
          if (cartDrawer.classList.contains('open')) trapFocus(cartDrawer);
        }
      });
    });
    cartObserver.observe(cartDrawer, { attributes: true });
  }

  /* ─────────────────────────────────────────────────────────
     13. EXPOSE PUBLIC API
  ───────────────────────────────────────────────────────── */

  AA.fetchCart       = fetchCart;
  AA.updateCartLine  = updateCartLine;
  AA.updateCartBadge = updateCartBadge;
  AA.formatMoney     = formatMoney;
  AA.initScrollReveal = initScrollReveal;

  window.AutoAdrenaline = AA;

})();


/* ── MOBILE CATEGORY DROPDOWN ─────────────────────────────── */
(function() {
  'use strict';

  function initMobileCategoryToggle() {
    const toggle = document.querySelector('.site-nav__mobile-category-toggle');
    const list = document.getElementById('mobile-category-list');

    if (!toggle || !list) return;

    // Wrap list contents in a div for grid animation
    const inner = document.createElement('div');
    while (list.firstChild) {
      inner.appendChild(list.firstChild);
    }
    list.appendChild(inner);

    toggle.addEventListener('click', function() {
      const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
      const newState = !isExpanded;
      toggle.setAttribute('aria-expanded', String(newState));
      list.setAttribute('aria-hidden', String(!newState));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initMobileCategoryToggle);
  } else {
    initMobileCategoryToggle();
  }
})();
